import { useInfiniteQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';
import CommentsApi from 'app/modules/comments/api';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import { useMemo } from 'react';

type Props = {
  postId: number;
  thread: PostCommunicationContent;
};

export const useCommentsThreadQuery = ({ postId, thread }: Props) => {
  const comments = useInfiniteQuery(
    queryKey.postCommentsThread({
      postId: postId,
      thread: thread,
    }),
    async ({ pageParam = 1 }) => {
      if (
        thread === PostCommunicationContent.ACTIVITY ||
        thread === PostCommunicationContent.VERSIONS
      ) {
        throw new Error('Only internal and client threads are supported');
      }

      return CommentsApi.comments(postId, {
        thread: thread !== PostCommunicationContent.PINNED ? thread : undefined,
        pinned: thread === PostCommunicationContent.PINNED ? 1 : undefined,
        page: pageParam,
      });
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.pagination.pageCount > allPages.length
          ? allPages.length + 1
          : undefined;
      },
    },
  );

  const data = useMemo(() => {
    return comments.data?.pages.flatMap(({ data }) => data) ?? [];
  }, [comments.data]);

  const [ref, { rootRef }] = useInfiniteScroll({
    loading: comments.isLoading,
    hasNextPage: !!comments.hasNextPage,
    onLoadMore: () => {
      comments.fetchNextPage();
    },
  });

  return {
    data,
    isLoading: comments.isLoading,
    isFetching: comments.isFetching,
    isError: comments.isError,
    error: comments.error,
    hasNextPage: comments.hasNextPage,
    ref,
    rootRef,
  };
};
